/**
 * Globo Product Options カスタマイズ
 *
 * 機能:
 * - 木札商品専用のライブプレビュー
 * - モバイル対応の横スクロール
 * - ドロップダウン検索機能
 * - カスタムラベル表示
 *
 * @version 2.0
 */

(function () {
  "use strict";

  // ============================================
  // 定数定義
  // ============================================
  const CONSTANTS = {
    MOBILE_BREAKPOINT: 699,
    SELECTORS: {
      BUY_BUTTONS: 'buy-buttons[template="kifuda"]',
      OPTION_WRAPPER: ".gpo-ov-wrapper.horizontal",
      IMAGE_SWATCHES:
        '[data-element-id="image-swatches-5"], [data-element-id="image-swatches-6"]',
      COLOR_SWATCHES: ".color-swatches",
    },
  };

  // ============================================
  // ユーティリティ関数
  // ============================================
  const isMobile = () => window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT;

  const isKifudaProduct = () => {
    return document.querySelector(CONSTANTS.SELECTORS.BUY_BUTTONS) !== null;
  };

  // ============================================
  // メイン初期化
  // ============================================
  document.addEventListener("DOMContentLoaded", function () {
    // 木札商品かどうか判定
    if (!isKifudaProduct()) return;

    // ローディングオーバーレイを表示
    showLoadingOverlay();

    // ライブプレビュー機能の初期化
    try {
      initKifudaLivePreview();
    } catch (error) {
      console.error("ライブプレビュー機能の初期化エラー:", error);
    }

    // ドロップダウン検索機能の初期化
    try {
      initDropdownSearch();
    } catch (error) {
      console.error("ドロップダウン検索機能の初期化エラー:", error);
    }

    // スクロール機能の初期化（PC・SP共通）
    initMobileScrollFeatures();

    // ローディング完了を監視
    waitForOptionsLoaded();
  });

  // ============================================
  // ローディングオーバーレイ
  // ============================================
  function showLoadingOverlay() {
    // 既存のオーバーレイがあれば削除
    const existing = document.querySelector(".kifuda-loading-overlay");
    if (existing) existing.remove();

    const overlay = document.createElement("div");
    overlay.className = "kifuda-loading-overlay";
    overlay.innerHTML = `
      <div class="kifuda-loading-spinner"></div>
      <div class="kifuda-loading-text">読み込み中...</div>
    `;
    document.body.appendChild(overlay);
  }

  function hideLoadingOverlay() {
    const overlay = document.querySelector(".kifuda-loading-overlay");
    if (overlay) {
      overlay.classList.add("hidden");
      setTimeout(() => overlay.remove(), 300);
    }
  }

  function waitForOptionsLoaded() {
    // Globo Product Optionsの読み込み完了を確認
    let checkCount = 0;
    const maxChecks = 50; // 最大5秒待機

    const checkInterval = setInterval(() => {
      checkCount++;

      // オプション要素が読み込まれたかチェック
      const gpoElements = document.querySelectorAll(
        ".gpo-element, .gpo-swatches, .gpo-dropdown"
      );
      const previewContainer = document.querySelector(
        ".kifuda-preview-container"
      );

      if (
        (gpoElements.length > 0 && previewContainer) ||
        checkCount >= maxChecks
      ) {
        clearInterval(checkInterval);
        // 少し遅延してからローディングを非表示（アニメーション完了を待つ）
        setTimeout(hideLoadingOverlay, 300);
      }
    }, 100);
  }

  // ============================================
  // モバイル横スクロール機能
  // ============================================
  function initMobileScrollFeatures() {
    const optionWrappers = document.querySelectorAll(
      CONSTANTS.SELECTORS.OPTION_WRAPPER
    );

    optionWrappers.forEach((wrapper) => {
      setupDragScroll(wrapper);
      addPriceLabels();
      addColorLabels();
    });
  }

  /**
   * ドラッグスクロール機能を設定
   */
  function setupDragScroll(wrapper) {
    wrapper.style.scrollBehavior = "smooth";

    let dragState = {
      isDown: false,
      startX: 0,
      scrollLeft: 0,
    };

    const setCursor = (cursor) => (wrapper.style.cursor = cursor);

    wrapper.addEventListener("mousedown", (e) => {
      dragState.isDown = true;
      setCursor("grabbing");
      dragState.startX = e.pageX - wrapper.offsetLeft;
      dragState.scrollLeft = wrapper.scrollLeft;
    });

    wrapper.addEventListener("mouseleave", () => {
      dragState.isDown = false;
      setCursor("grab");
    });

    wrapper.addEventListener("mouseup", () => {
      dragState.isDown = false;
      setCursor("grab");
    });

    wrapper.addEventListener("mousemove", (e) => {
      if (!dragState.isDown) return;
      e.preventDefault();
      const x = e.pageX - wrapper.offsetLeft;
      const walk = (x - dragState.startX) * 2;
      wrapper.scrollLeft = dragState.scrollLeft - walk;
    });

    if (wrapper.scrollWidth > wrapper.clientWidth) {
      setCursor("grab");
    }
  }

  // ============================================
  // カスタムラベル機能
  // ============================================

  /**
   * 画像スウォッチに価格ラベルを追加
   */
  function addPriceLabels() {
    const imageSwatchesElements = document.querySelectorAll(
      CONSTANTS.SELECTORS.IMAGE_SWATCHES
    );

    imageSwatchesElements.forEach((imageSwatchesElement) => {
      const swatches = imageSwatchesElement.querySelectorAll(".gpo-swatches");

      swatches.forEach((swatch) => {
        // 既存のラベルを削除
        removeExistingLabels(swatch);

        const tooltip = swatch.querySelector(".gpo-tooltip");
        let titleText = "";
        let priceText = "";

        if (tooltip) {
          // ツールチップから情報を取得
          const info = extractTooltipInfo(tooltip);
          titleText = info.titleText;
          priceText = info.priceText;
        } else {
          // ツールチップがない場合、labelから直接取得
          const label = swatch.querySelector("label");
          const input = swatch.querySelector("input");

          if (label) {
            const breakWord = label.querySelector(".break-word");
            const addOn = label.querySelector(".addOn");

            if (breakWord) {
              titleText = breakWord.textContent?.trim() || "";
            } else {
              titleText = label.textContent?.trim() || "";
              // addOnテキストを除去
              if (addOn) {
                titleText = titleText.replace(addOn.textContent, "").trim();
              }
            }

            priceText = addOn?.textContent?.trim() || "+¥0";

            // 価格テキストの整形
            priceText = priceText
              .replace(/[()]/g, "")
              .trim()
              .replace(/\s*\+\s*/, "\n+");
          }

          // 「文字一行」を最後の行に移動
          if (titleText.includes("文字一行")) {
            let lines = titleText.split(/[\n＋]/);
            const mojiGyoIndex = lines.findIndex((line) =>
              line.includes("文字一行")
            );
            if (mojiGyoIndex !== -1) {
              const mojiGyoLine = lines.splice(mojiGyoIndex, 1)[0];
              lines.push(mojiGyoLine);
            }
            titleText = lines.join("＋");
          }

          titleText = titleText.replace(/＋/g, "\n");
        }

        // ラベルを作成して追加
        if (titleText) {
          createAndAppendLabels(swatch, titleText, priceText);
        }
      });
    });
  }

  /**
   * 既存のカスタムラベルを削除
   */
  function removeExistingLabels(swatch) {
    const existingTitleLabel = swatch.querySelector(".custom-label-title");
    const existingPriceLabel = swatch.querySelector(".custom-label-price");
    existingTitleLabel?.remove();
    existingPriceLabel?.remove();
  }

  /**
   * ツールチップから情報を抽出
   */
  function extractTooltipInfo(tooltip) {
    // 一時的に表示して情報取得
    const originalStyles = {
      display: tooltip.style.display,
      visibility: tooltip.style.visibility,
      opacity: tooltip.style.opacity,
      position: tooltip.style.position,
    };

    tooltip.style.display = "block";
    tooltip.style.visibility = "visible";
    tooltip.style.opacity = "1";
    tooltip.style.position = "absolute";

    const tooltipTitle = tooltip.querySelector(".gpo-tooltip__title");
    let titleText = "";
    let priceText = "";

    if (tooltipTitle) {
      const breakWord = tooltipTitle.querySelector(".break-word");
      const addOn = tooltipTitle.querySelector(".addOn");

      titleText = breakWord?.textContent?.trim() || "";
      priceText = addOn?.textContent?.trim() || "";

      // 「文字一行」を最後の行に移動
      if (titleText.includes("文字一行")) {
        let lines = titleText.split(/[\n＋]/);
        const mojiGyoIndex = lines.findIndex((line) =>
          line.includes("文字一行")
        );
        if (mojiGyoIndex !== -1) {
          const mojiGyoLine = lines.splice(mojiGyoIndex, 1)[0];
          lines.push(mojiGyoLine);
        }
        titleText = lines.join("＋");
      }

      // 価格テキストの整形
      priceText = priceText
        ? priceText
            .replace(/[()]/g, "")
            .trim()
            .replace(/\s*\+\s*/, "\n+")
        : "+¥0";
    }

    // スタイルを元に戻す
    Object.assign(tooltip.style, originalStyles);

    return {
      titleText: titleText.replace(/＋/g, "\n"),
      priceText,
    };
  }

  /**
   * ラベル要素を作成して追加
   */
  function createAndAppendLabels(swatch, titleText, priceText) {
    const titleLabel = document.createElement("div");
    titleLabel.className = "custom-label-title";
    titleLabel.textContent = titleText;

    const priceLabel = document.createElement("div");
    priceLabel.className = "custom-label-price";
    priceLabel.textContent = priceText;
    swatch.insertBefore(titleLabel, swatch.firstChild);

    // swatchの最後に価格を追加（画像の下に表示）
    swatch.insertBefore(titleLabel, swatch.firstChild);
    swatch.appendChild(priceLabel);
  }

  /**
   * カラースウォッチに色名ラベルを追加
   */
  function addColorLabels() {
    // カラースウォッチと紐の種類・紐の色を含む全てのスウォッチを取得
    const allSwatches = document.querySelectorAll(
      '.color-swatches, [data-element-id="image-swatches-9"] .gpo-swatches, [data-element-id="image-swatches-10"] .gpo-swatches, [data-element-id="image-swatches-11"] .gpo-swatches'
    );

    allSwatches.forEach((swatch) => {
      // 既存のラベルがあればスキップ（重複追加を防ぐ）
      if (swatch.querySelector(".custom-color-label")) return;

      // inputのvalue属性から値を取得
      const input = swatch.querySelector("input");
      if (!input) return;

      const valueText = input.getAttribute("value") || input.value || "";

      // カラースウォッチかどうかを判定（紐の種類・紐の色ではない）
      const isColorSwatch = swatch.classList.contains("color-swatches");

      // 価格情報を取得（カラースウォッチのみ）
      let priceText = "";
      if (isColorSwatch) {
        const label = swatch.querySelector("label");
        if (label) {
          const addOn = label.querySelector(".addOn");
          if (addOn) {
            priceText = addOn.textContent?.trim() || "";
            // 価格テキストの整形
            priceText = priceText.replace(/[()]/g, "").trim();
          }
        }
      }

      if (valueText) {
        // 色名ラベルを追加
        const colorLabel = document.createElement("div");
        colorLabel.className = "custom-color-label";
        colorLabel.textContent = valueText;
        swatch.appendChild(colorLabel);

        // 価格ラベルを追加（カラースウォッチのみ、価格がない場合は+¥0を表示）
        if (isColorSwatch) {
          const priceLabel = document.createElement("div");
          priceLabel.className = "custom-color-price";
          priceLabel.textContent = priceText || "+¥0";
          swatch.appendChild(priceLabel);
        }
      }
    });
  }

  // 初回実行とリトライ（1回だけ）
  function initLabels() {
    addPriceLabels();
    addColorLabels();
  }

  initLabels();
  // 遅延読み込み対応で1回だけリトライ
  setTimeout(initLabels, 1500);

  // ============================================
  // DOM監視（木札商品のみ）- デバウンス付き
  // ============================================
  let labelDebounceTimer = null;
  const observer = new MutationObserver((mutations) => {
    // 木札商品でない場合は処理しない
    if (!isKifudaProduct()) return;

    let needsLabelUpdate = false;

    mutations.forEach((mutation) => {
      if (!mutation.addedNodes.length) return;

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        // カスタムラベルは無視（無限ループ防止）
        if (
          node.classList?.contains("custom-label-title") ||
          node.classList?.contains("custom-label-price") ||
          node.classList?.contains("custom-color-label")
        ) {
          return;
        }

        // 横スクロール要素を初期化
        if (
          node.classList?.contains("gpo-ov-wrapper") &&
          node.classList?.contains("horizontal")
        ) {
          setupDragScroll(node);
          needsLabelUpdate = true;
        }

        // 子要素もチェック
        const children = node.querySelectorAll(".gpo-ov-wrapper.horizontal");
        if (children.length > 0) {
          children.forEach((child) => setupDragScroll(child));
          needsLabelUpdate = true;
        }

        // gpo関連要素が追加された場合のみラベル更新フラグを立てる
        if (
          node.classList?.contains("gpo-swatches") ||
          node.querySelector?.(".gpo-swatches")
        ) {
          needsLabelUpdate = true;
        }
      });
    });

    // デバウンス処理でラベル更新
    if (needsLabelUpdate) {
      if (labelDebounceTimer) clearTimeout(labelDebounceTimer);
      labelDebounceTimer = setTimeout(() => {
        initLabels();
        labelDebounceTimer = null;
      }, 300);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();

// ============================================
// グローバル関数
// ============================================

/**
 * 木札ライブプレビュー機能
 */
function initKifudaLivePreview() {
  try {
    // プレビューコンテナの作成
    function createPreviewContainer() {
      // 既存のプレビューコンテナがあれば削除
      const existingPreview = document.querySelector(
        ".kifuda-preview-container"
      );
      if (existingPreview) {
        existingPreview.remove();
      }

      // Globo Product Optionsアプリブロックを探す
      const gpoBlock = document.querySelector(
        '.product-info__block-item[data-block-type="@app"]'
      );
      if (!gpoBlock) {
        // フォールバック: .product-form や他の要素を探す
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          // フォームの前に挿入してみる
          return insertPreviewContainer(productForm, "before");
        }
        return null;
      }
      return insertPreviewContainer(gpoBlock, "before");
    }

    function insertPreviewContainer(targetElement, position) {
      // 商品ギャラリーから最初の画像を取得
      let backgroundImageUrl = "";
      const galleryImage = document.querySelector(
        '.product-gallery img, .product__media img, [class*="product"] img[src*="cdn.shopify.com"]'
      );
      if (galleryImage) {
        backgroundImageUrl = galleryImage.src || galleryImage.dataset.src || "";
      }

      const previewContainer = document.createElement("div");
      previewContainer.className = "kifuda-preview-container";
      previewContainer.innerHTML = `
      <div class="kifuda-preview-title">
        <span>プレビュー</span>
        <button class="kifuda-preview-toggle" aria-label="プレビューを折りたたむ">▼</button>
      </div>
      <div class="kifuda-preview-wrapper" style="display: flex; gap: 10px;">
        <div class="kifuda-preview-side" style="flex: 1;">
          <div class="kifuda-preview-canvas kifuda-preview-front" style="background-image: url('${backgroundImageUrl}'); background-size: 290px; background-position: center;">
            <div class="kifuda-preview-label" style="position: absolute; top: -1px; left: -1px; font-size: 11px; background-color: #fff; padding: 2px 6px; color: #333; font-weight: 500; z-index: 10;">表面</div>
            <div class="kifuda-preview-placeholder">デザインを選択してください</div>
            <div class="kifuda-preview-kamon" style="display: none; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 45px; height: 45px; background-size: contain; background-repeat: no-repeat; background-position: center; z-index: 1;"></div>
            <div class="kifuda-preview-text kifuda-preview-text-1" style="display: none; z-index: 2;"></div>
            <div class="kifuda-preview-text kifuda-preview-text-2" style="display: none; z-index: 2;"></div>
          </div>
        </div>
        <div class="kifuda-preview-side kifuda-preview-side-back" style="flex: 1; display: none;">
          <div class="kifuda-preview-canvas kifuda-preview-back" style="background-image: url('${backgroundImageUrl}'); background-size: 290px; background-position: center;">
            <div class="kifuda-preview-label" style="position: absolute; top: -1px; left: -1px; font-size: 11px; background-color: #fff; padding: 2px 6px; color: #333; font-weight: 500; z-index: 10;">裏面</div>
            <div class="kifuda-preview-placeholder">デザインを選択してください</div>
            <div class="kifuda-preview-kamon" style="display: none; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 45px; height: 45px; background-size: contain; background-repeat: no-repeat; background-position: center; z-index: 1;"></div>
            <div class="kifuda-preview-text kifuda-preview-text-1" style="display: none; z-index: 2;"></div>
            <div class="kifuda-preview-text kifuda-preview-text-2" style="display: none; z-index: 2;"></div>
          </div>
        </div>
      </div>
      <div class="kifuda-preview-note">※画像はイメージです。全体のバランスを調整して制作いたします。</div>
    `;

      // previewContainerを囲むラッパーを作成
      const previewWrapper = document.createElement("div");
      previewWrapper.className = "kifuda-preview-wrapper-outer";
      previewWrapper.appendChild(previewContainer);

      // PC表示（1000px以上）の場合は.productの直下に挿入
      const isDesktop = window.innerWidth >= 1000;
      const productContainer = document.querySelector(".product");

      if (isDesktop && productContainer) {
        // .productの最初の子要素として挿入（左側に配置）
        productContainer.insertBefore(
          previewWrapper,
          productContainer.firstChild
        );
      } else {
        // SP表示の場合は従来通りtargetElementの前に挿入
        if (position === "before") {
          targetElement.parentElement.insertBefore(
            previewWrapper,
            targetElement
          );
        } else {
          targetElement.appendChild(previewWrapper);
        }
      }

      // 折りたたみボタンの機能を追加
      const toggleButton = previewContainer.querySelector(
        ".kifuda-preview-toggle"
      );
      const previewTitle = previewContainer.querySelector(
        ".kifuda-preview-title"
      );

      // ボタン自体のクリックを無効化
      toggleButton.style.pointerEvents = "none";

      function togglePreview() {
        previewContainer.classList.toggle("collapsed");
        if (previewContainer.classList.contains("collapsed")) {
          toggleButton.textContent = "▲";
          toggleButton.setAttribute("aria-label", "プレビューを展開する");
        } else {
          toggleButton.textContent = "▼";
          toggleButton.setAttribute("aria-label", "プレビューを折りたたむ");
        }
      }

      // タイトル全体をクリック可能に（モバイルサイズのみ）
      // ※ MOBILE_BREAKPOINT: 699px
      function updateTitleClickability() {
        const isMobile = window.innerWidth <= 699;
        if (isMobile) {
          previewTitle.style.cursor = "pointer";
        } else {
          previewTitle.style.cursor = "default";
        }
      }

      // 初期設定
      updateTitleClickability();

      // リサイズ時に更新
      window.addEventListener("resize", updateTitleClickability);

      previewTitle.addEventListener("click", function (e) {
        const isMobile = window.innerWidth <= 699;
        if (isMobile) {
          e.stopPropagation();
          togglePreview();
        }
      });

      return previewContainer;
    }

    // プレビューコンテナの作成
    const previewContainer = createPreviewContainer();
    if (!previewContainer) return;

    // プレビュー要素の取得（表面）
    const previewCanvasFront = previewContainer.querySelector(
      ".kifuda-preview-front"
    );
    const previewPlaceholderFront = previewCanvasFront.querySelector(
      ".kifuda-preview-placeholder"
    );
    const previewText1Front = previewCanvasFront.querySelector(
      ".kifuda-preview-text-1"
    );
    const previewText2Front = previewCanvasFront.querySelector(
      ".kifuda-preview-text-2"
    );
    const previewKamonFront = previewCanvasFront.querySelector(
      ".kifuda-preview-kamon"
    );

    // プレビュー要素の取得（裏面）
    const previewCanvasBack = previewContainer.querySelector(
      ".kifuda-preview-back"
    );

    const previewPlaceholderBack = previewCanvasBack?.querySelector(
      ".kifuda-preview-placeholder"
    );
    const previewText1Back = previewCanvasBack?.querySelector(
      ".kifuda-preview-text-1"
    );
    const previewText2Back = previewCanvasBack?.querySelector(
      ".kifuda-preview-text-2"
    );
    const previewKamonBack = previewCanvasBack?.querySelector(
      ".kifuda-preview-kamon"
    );

    // 裏面プレビューの表示制御
    const backPreviewSide = previewContainer.querySelector(
      ".kifuda-preview-side-back"
    );

    function updateBackPreviewVisibility() {
      // ラジオボタンで「あり」が選択されているかチェック
      const backRadioYes = document.querySelector(
        'input[name="裏面書き追加"][value="あり"]'
      );

      if (backRadioYes && backPreviewSide) {
        if (backRadioYes.checked) {
          backPreviewSide.style.display = "flex";
        } else {
          backPreviewSide.style.display = "none";
        }
      }
    }

    // ラジオボタンの監視を設定（少し遅延させる）
    setTimeout(() => {
      const backRadios = document.querySelectorAll(
        'input[name="裏面書き追加"]'
      );

      if (backRadios.length > 0) {
        // 初回チェック
        updateBackPreviewVisibility();

        // 全てのラジオボタンにchangeイベントを監視
        backRadios.forEach((radio) => {
          radio.addEventListener("change", updateBackPreviewVisibility);
        });
      }
    }, 1000);

    // 商品名を取得して黒檀商品かどうかを判定
    function isKokutanProduct() {
      const productTitle = document.querySelector(".product-title");
      if (productTitle) {
        return productTitle.textContent.includes("黒檀");
      }
      return false;
    }

    // 黒檀商品の場合は白色、それ以外は黒色をデフォルトに
    const defaultColor = isKokutanProduct() ? "#FFFFFF" : "#000000";

    // 選択状態を保持（表面）
    const selectedOptions = {
      text: "",
      text2: "", // 2つ目のテキスト用
      color1: defaultColor, // text1用の色
      color2: defaultColor, // text2用の色
      borderColor: defaultColor, // 枠線の色（差札用）
      backgroundColor: "#FFFFFF", // 背景色（色差札用）
      fontSize: "40px",
      scaleX: 1,
      scaleY: 1,
      design: "", // 選択されたデザイン名
      kamonImage: "", // 家紋画像のURL
      kamonColor: defaultColor, // 家紋の色
    };

    // 選択状態を保持（裏面）
    const selectedOptionsBack = {
      text: "",
      text2: "",
      color1: defaultColor,
      color2: defaultColor,
      borderColor: defaultColor,
      backgroundColor: "#FFFFFF",
      fontSize: "40px",
      scaleX: 1,
      scaleY: 1,
      design: "",
      kamonImage: "",
      kamonColor: defaultColor,
    };

    /**
     * デザイン変更時に入力値をクリアする
     * @param {boolean} isBack - 裏面かどうか
     */
    function clearInputValues(isBack) {
      const options = isBack ? selectedOptionsBack : selectedOptions;
      const fieldKeyword = isBack ? "裏面" : "";

      // 状態変数をリセット（designは保持）
      const savedDesign = options.design;
      options.text = "";
      options.text2 = "";

      // 黒檀商品で色差札・色千社札の場合は文字色を黒に、それ以外は defaultColor
      const isColoredDesign =
        savedDesign &&
        (savedDesign.includes("色差札") || savedDesign.includes("色千社札"));
      const textColorForDesign =
        isKokutanProduct() && isColoredDesign ? "#000000" : defaultColor;

      options.color1 = textColorForDesign;
      options.color2 = textColorForDesign;
      options.borderColor = textColorForDesign;
      options.backgroundColor = "#FFFFFF";
      options.kamonImage = "";
      options.kamonColor = defaultColor;
      options.scaleX = 1;
      options.scaleY = 1;
      options.design = savedDesign;

      // DOM要素のテキスト入力をクリア
      const textInputs = document.querySelectorAll(
        'input[type="text"].gpo-input, textarea.gpo-input'
      );
      textInputs.forEach((input) => {
        const inputFieldName = input.dataset.fieldName || input.name || "";
        const inputIsBack = inputFieldName.includes("裏面");
        // 紐関連のフィールドは除外
        if (inputFieldName.includes("紐")) {
          return;
        }
        // 表面/裏面が一致する場合のみクリア
        if (isBack === inputIsBack) {
          input.value = "";
          // GPO内部の状態も更新するためイベントを発火
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });

      // 色選択（スウォッチ）のリセット - 選択状態を解除
      const colorSwatches = document.querySelectorAll(
        '.color-swatches input[type="radio"], ' +
          '[data-element-id^="image-swatches-"] input[type="radio"]'
      );
      colorSwatches.forEach((input) => {
        const inputFieldName = input.dataset.fieldName || input.name || "";
        const inputIsBack = inputFieldName.includes("裏面");
        // デザイン選択（image-swatches-5, image-swatches-6）は除外
        const parentElement = input.closest("[data-element-id]");
        const elementId = parentElement?.dataset.elementId || "";
        if (
          elementId === "image-swatches-5" ||
          elementId === "image-swatches-6"
        ) {
          return; // デザイン選択スウォッチは除外
        }
        // 紐関連のフィールドは除外
        if (inputFieldName.includes("紐")) {
          return;
        }
        // 表面/裏面が一致する場合のみリセット
        if (isBack === inputIsBack) {
          input.checked = false;
        }
      });

      // ドロップダウン（家紋・梵字選択）のリセット
      const dropdowns = document.querySelectorAll(".gpo-dropdown");
      dropdowns.forEach((dropdown) => {
        const items = dropdown.querySelectorAll(
          'input[type="radio"], input[type="checkbox"]'
        );
        if (items.length === 0) return;

        const firstInput = items[0];
        const firstFieldName =
          firstInput.dataset.fieldName || firstInput.name || "";
        const dropdownIsBack = firstFieldName.includes("裏面");

        // 紐関連のフィールドは除外
        if (firstFieldName.includes("紐")) {
          return;
        }
        // 表面/裏面が一致する場合のみリセット
        if (isBack === dropdownIsBack) {
          items.forEach((input) => {
            input.checked = false;
          });
        }
      });
    }

    // デザインごとの設定（リファクタリング v2）
    const designConfigs = {
      家紋: {
        textCount: 0,
        kamon: {
          position: { top: "50%", left: "50%" },
          size: { width: 45, height: 45 },
        },
      },
      梵字: {
        textCount: 0,
        kamon: {
          position: { top: "50%", left: "50%" },
          size: { width: 48, height: 48 },
        },
      },
      差札: {
        textCount: 1,
        text1: {
          position: { top: "calc(50% + 5px)", left: "50%" },
          size: { width: 20, height: 80 },
          writingMode: "vertical-rl",
          rotation: 5,
          applyStyles: (element, options) => {
            element.classList.add("sashifuda-text");
            element.style.padding = "0";
            element.style.border = `2px solid ${options.borderColor}`;
          },
        },
      },
      色差札: {
        textCount: 1,
        text1: {
          position: { top: "calc(50% + 5px)", left: "50%" },
          size: { width: 20, height: 80 },
          writingMode: "vertical-rl",
          rotation: 5,
          applyStyles: (element, options) => {
            element.style.backgroundColor = options.backgroundColor;
            element.style.padding = "2px";
          },
        },
      },
      千社札: {
        textCount: 1,
        text1: {
          position: { top: "calc(50% + 5px)", left: "50%" },
          size: { width: 16, height: 80 },
          writingMode: "vertical-rl",
          rotation: 5,
          applyStyles: (element, options) => {
            element.classList.add("senshafuda-double-border");
            element.style.cssText += `padding: 10px; --border-color: ${options.borderColor};`;

            // 文字数に応じて位置を調整
            const len = element.textContent.length;
            const offsets =
              len === 1
                ? ["2px", "5px"]
                : len === 2
                ? ["-2px", "4px"]
                : len === 3
                ? ["-6px", "3px"]
                : ["-10px", "2px"];
            element.style.setProperty("--senshafuda-top-offset", offsets[0]);
            element.style.setProperty("--senshafuda-bottom-offset", offsets[1]);
          },
        },
      },
      色千社札: {
        textCount: 1,
        text1: {
          position: { top: "calc(50% + 5px)", left: "50%" },
          size: { width: 16, height: 80 },
          writingMode: "vertical-rl",
          rotation: 5,
          applyStyles: (element, options) => {
            element.classList.add("senshafuda-filled-double-border");
            element.style.cssText += `padding: 10px; --border-color: ${options.borderColor}; --background-color: ${options.backgroundColor};`;

            // 文字数に応じて位置を調整
            const len = element.textContent.length;
            const offsets =
              len === 1
                ? ["2px", "5px"]
                : len === 2
                ? ["-2px", "4px"]
                : len === 3
                ? ["-6px", "3px"]
                : ["-10px", "2px"];
            element.style.setProperty("--senshafuda-top-offset", offsets[0]);
            element.style.setProperty("--senshafuda-bottom-offset", offsets[1]);
          },
        },
      },
      文字一行: {
        textCount: 1,
        text1: {
          position: { top: "calc(50% + 10px)", left: "50%" },
          size: { width: 50, height: 120 },
          writingMode: "vertical-rl",
          rotation: 0,
          // スタイル適用なし（シンプルなテキストのみ）
        },
      },
      "文字一行＋横書": {
        textCount: 2,
        text1: {
          position: { top: "65%", left: "50%" },
          size: { width: 48, height: 95 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "26%", left: "50%" },
          size: { width: 48, height: 25 },
          writingMode: "horizontal-tb",
          rotation: 0,
        },
      },
      "文字一行＋一行追加": {
        textCount: 2,
        text1: {
          position: { top: "57%", left: "45%" },
          size: { width: 32, height: 122 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "51%", left: "60%" },
          size: { width: 15, height: 110 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
      },
      "文字一行＋差札": {
        textCount: 2,
        text1: {
          position: { top: "calc(50% + 10px)", left: "50%" },
          size: { width: 50, height: 120 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "calc(50% + 5px)", left: "50%" },
          size: { width: 15, height: 50 },
          writingMode: "vertical-rl",
          rotation: 30,
          applyStyles: (element, options, previewCanvas) => {
            element.classList.add("sashifuda-text");
            element.style.padding = "0";
            const borderWidth = "2px";
            element.style.borderStyle = "solid";
            element.style.borderColor = options.borderColor;
            element.style.borderTopWidth = `calc(${borderWidth} / var(--scale-y, 1))`;
            element.style.borderBottomWidth = `calc(${borderWidth} / var(--scale-y, 1))`;
            element.style.borderLeftWidth = `calc(${borderWidth} / var(--scale-x, 1))`;
            element.style.borderRightWidth = `calc(${borderWidth} / var(--scale-x, 1))`;

            // 背景画像をキャンバスと同じにする（下の文字を隠すため）
            element.style.backgroundImage = previewCanvas.style.backgroundImage;

            // 背景画像の縦横比を取得して高さを補正
            let aspectRatio = 1;
            const galleryImage = document.querySelector(
              '.product-gallery img, .product__media img, [class*="product"] img[src*="cdn.shopify.com"]'
            );
            if (galleryImage && galleryImage.naturalWidth > 0) {
              aspectRatio =
                galleryImage.naturalHeight / galleryImage.naturalWidth;
            }

            element.style.backgroundSize = `calc(280px / var(--scale-x, 1)) calc((280px * ${aspectRatio}) / var(--scale-y, 1))`;
            element.style.backgroundPosition = "center";
            element.style.backgroundRepeat = "no-repeat";
            element.style.backgroundColor = "transparent";
          },
        },
      },
      "文字一行＋色差札": {
        textCount: 2,
        text1: {
          position: { top: "calc(50% + 10px)", left: "50%" },
          size: { width: 50, height: 120 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "calc(50% + 5px)", left: "50%" },
          size: { width: 15, height: 50 },
          writingMode: "vertical-rl",
          rotation: 30,
          applyStyles: (element, options) => {
            element.style.backgroundColor = options.backgroundColor;
            element.style.padding = "2px";
          },
        },
      },
      "文字一行＋家紋": {
        textCount: 1,
        text1: {
          position: { top: "68%", left: "50%" },
          size: { width: 48, height: 85 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        kamon: {
          position: { top: "28%", left: "50%" },
          size: { width: 38, height: 38 },
        },
      },
      "文字一行＋家紋＋差札": {
        textCount: 2,
        text1: {
          position: { top: "68%", left: "50%" },
          size: { width: 48, height: 85 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "calc(50% + 30px)", left: "50%" },
          size: { width: 12, height: 40 },
          writingMode: "vertical-rl",
          rotation: 30,
          applyStyles: (element, options, previewCanvas) => {
            element.classList.add("sashifuda-text");
            element.style.padding = "0";
            const borderWidth = "2px";
            element.style.borderStyle = "solid";
            element.style.borderColor = options.borderColor;
            element.style.borderTopWidth = `calc(${borderWidth} / var(--scale-y, 1))`;
            element.style.borderBottomWidth = `calc(${borderWidth} / var(--scale-y, 1))`;
            element.style.borderLeftWidth = `calc(${borderWidth} / var(--scale-x, 1))`;
            element.style.borderRightWidth = `calc(${borderWidth} / var(--scale-x, 1))`;

            // 背景画像をキャンバスと同じにする（下の文字を隠すため）
            element.style.backgroundImage = previewCanvas.style.backgroundImage;

            // 背景画像の縦横比を取得して高さを補正
            let aspectRatio = 1;
            const galleryImage = document.querySelector(
              '.product-gallery img, .product__media img, [class*="product"] img[src*="cdn.shopify.com"]'
            );
            if (galleryImage && galleryImage.naturalWidth > 0) {
              aspectRatio =
                galleryImage.naturalHeight / galleryImage.naturalWidth;
            }

            element.style.backgroundSize = `calc(280px / var(--scale-x, 1)) calc((280px * ${aspectRatio}) / var(--scale-y, 1))`;
            element.style.backgroundPosition = "center";
            element.style.backgroundRepeat = "no-repeat";
            element.style.backgroundColor = "transparent";
          },
        },
        kamon: {
          position: { top: "28%", left: "50%" },
          size: { width: 38, height: 38 },
        },
      },
      "文字一行＋家紋＋色差札": {
        textCount: 2,
        text1: {
          position: { top: "68%", left: "50%" },
          size: { width: 48, height: 85 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "calc(50% + 30px)", left: "50%" },
          size: { width: 12, height: 40 },
          writingMode: "vertical-rl",
          rotation: 30,
          applyStyles: (element, options) => {
            element.style.backgroundColor = options.backgroundColor;
            element.style.padding = "2px";
          },
        },
        kamon: {
          position: { top: "28%", left: "50%" },
          size: { width: 38, height: 38 },
        },
      },
      "文字一行＋梵字": {
        textCount: 1,
        text1: {
          position: { top: "68%", left: "50%" },
          size: { width: 48, height: 85 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        kamon: {
          position: { top: "28%", left: "50%" },
          size: { width: 41, height: 41 },
        },
      },
      "文字一行＋梵字＋差札": {
        textCount: 2,
        text1: {
          position: { top: "68%", left: "50%" },
          size: { width: 48, height: 85 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "calc(50% + 30px)", left: "50%" },
          size: { width: 12, height: 40 },
          writingMode: "vertical-rl",
          rotation: 30,
          applyStyles: (element, options, previewCanvas) => {
            element.classList.add("sashifuda-text");
            element.style.padding = "0";
            const borderWidth = "2px";
            element.style.borderStyle = "solid";
            element.style.borderColor = options.borderColor;
            element.style.borderTopWidth = `calc(${borderWidth} / var(--scale-y, 1))`;
            element.style.borderBottomWidth = `calc(${borderWidth} / var(--scale-y, 1))`;
            element.style.borderLeftWidth = `calc(${borderWidth} / var(--scale-x, 1))`;
            element.style.borderRightWidth = `calc(${borderWidth} / var(--scale-x, 1))`;

            // 背景画像をキャンバスと同じにする（下の文字を隠すため）
            element.style.backgroundImage = previewCanvas.style.backgroundImage;

            // 背景画像の縦横比を取得して高さを補正
            let aspectRatio = 1;
            const galleryImage = document.querySelector(
              '.product-gallery img, .product__media img, [class*="product"] img[src*="cdn.shopify.com"]'
            );
            if (galleryImage && galleryImage.naturalWidth > 0) {
              aspectRatio =
                galleryImage.naturalHeight / galleryImage.naturalWidth;
            }

            element.style.backgroundSize = `calc(280px / var(--scale-x, 1)) calc((280px * ${aspectRatio}) / var(--scale-y, 1))`;
            element.style.backgroundPosition = "center";
            element.style.backgroundRepeat = "no-repeat";
            element.style.backgroundColor = "transparent";
          },
        },
        kamon: {
          position: { top: "28%", left: "50%" },
          size: { width: 41, height: 41 },
        },
      },
      "文字一行＋梵字＋色差札": {
        textCount: 2,
        text1: {
          position: { top: "68%", left: "50%" },
          size: { width: 48, height: 85 },
          writingMode: "vertical-rl",
          rotation: 0,
        },
        text2: {
          position: { top: "calc(50% + 30px)", left: "50%" },
          size: { width: 12, height: 40 },
          writingMode: "vertical-rl",
          rotation: 30,
          applyStyles: (element, options) => {
            element.style.backgroundColor = options.backgroundColor;
            element.style.padding = "2px";
          },
        },
        kamon: {
          position: { top: "28%", left: "50%" },
          size: { width: 38, height: 38 },
        },
      },
    };

    // プレビューを更新（位置・スケールには触れない）
    function updatePreview() {
      const config = designConfigs[selectedOptions.design];
      let hasContent = false;

      if (config) {
        // === text1のリセットとコンテンツ設定 ===
        previewText1Front.classList.remove(
          "sashifuda-text",
          "senshafuda-double-border",
          "senshafuda-filled-double-border"
        );
        previewText1Front.style.border = "none";
        previewText1Front.style.backgroundColor = "transparent";
        previewText1Front.style.padding = "0";

        if (selectedOptions.text && selectedOptions.text.trim() !== "") {
          previewText1Front.textContent = selectedOptions.text.replace(
            /[\r\n]+/g,
            ""
          );
          previewText1Front.style.color = selectedOptions.color1;
          previewText1Front.style.display = "block";

          if (config.text1 && config.text1.applyStyles) {
            config.text1.applyStyles(
              previewText1Front,
              selectedOptions,
              previewCanvasFront
            );
          }

          hasContent = true;
        } else {
          previewText1Front.style.display = "none";
        }

        // === text2のリセットとコンテンツ設定 ===
        if (config.textCount === 2) {
          previewText2Front.classList.remove("sashifuda-text");
          previewText2Front.style.border = "none";
          previewText2Front.style.backgroundColor = "transparent";
          previewText2Front.style.backgroundImage = "none";
          previewText2Front.style.padding = "0";

          if (selectedOptions.text2 && selectedOptions.text2.trim() !== "") {
            previewText2Front.textContent = selectedOptions.text2.replace(
              /[\r\n]+/g,
              ""
            );
            previewText2Front.style.color = selectedOptions.color2;
            previewText2Front.style.display = "block";

            if (config.text2 && config.text2.applyStyles) {
              config.text2.applyStyles(
                previewText2Front,
                selectedOptions,
                previewCanvasFront
              );
            }

            hasContent = true;
          } else {
            previewText2Front.style.display = "none";
          }
        } else {
          previewText2Front.style.display = "none";
        }
      } else {
        // デザイン未選択時はテキスト非表示
        previewText1Front.style.display = "none";
        previewText2Front.style.display = "none";
      }

      // === 家紋画像の表示 ===
      if (
        selectedOptions.kamonImage &&
        (selectedOptions.design === "家紋" ||
          selectedOptions.design === "梵字" ||
          selectedOptions.design === "文字一行＋家紋" ||
          selectedOptions.design === "文字一行＋家紋＋差札" ||
          selectedOptions.design === "文字一行＋家紋＋色差札" ||
          selectedOptions.design === "文字一行＋梵字" ||
          selectedOptions.design === "文字一行＋梵字＋差札" ||
          selectedOptions.design === "文字一行＋梵字＋色差札")
      ) {
        previewKamonFront.style.display = "block";

        // 家紋の色を適用（mask-imageを使用）
        if (
          selectedOptions.kamonColor &&
          selectedOptions.kamonColor !== "#000000"
        ) {
          // 画像をマスクとして使用し、背景色で色を適用
          previewKamonFront.style.backgroundColor = selectedOptions.kamonColor;
          previewKamonFront.style.webkitMaskImage = `url('${selectedOptions.kamonImage}')`;
          previewKamonFront.style.maskImage = `url('${selectedOptions.kamonImage}')`;
          previewKamonFront.style.webkitMaskSize = "contain";
          previewKamonFront.style.maskSize = "contain";
          previewKamonFront.style.webkitMaskRepeat = "no-repeat";
          previewKamonFront.style.maskRepeat = "no-repeat";
          previewKamonFront.style.webkitMaskPosition = "center";
          previewKamonFront.style.maskPosition = "center";
          previewKamonFront.style.backgroundImage = "none";
        } else {
          // 黒の場合はデフォルト（通常の画像表示）
          previewKamonFront.style.backgroundImage = `url('${selectedOptions.kamonImage}')`;
          previewKamonFront.style.backgroundColor = "transparent";
          previewKamonFront.style.webkitMaskImage = "none";
          previewKamonFront.style.maskImage = "none";
        }

        // 家紋の位置調整
        if (config && config.kamon) {
          previewKamonFront.style.top = config.kamon.position.top;
          previewKamonFront.style.left = config.kamon.position.left;
          previewKamonFront.style.width = config.kamon.size.width + "px";
          previewKamonFront.style.height = config.kamon.size.height + "px";
        } else {
          // デフォルト位置（中央）
          previewKamonFront.style.top = "50%";
          previewKamonFront.style.left = "50%";
          previewKamonFront.style.width = "45px";
          previewKamonFront.style.height = "45px";
        }

        hasContent = true;
      } else {
        previewKamonFront.style.display = "none";
        previewKamonFront.style.backgroundColor = "transparent";
        previewKamonFront.style.backgroundImage = "none";
        previewKamonFront.style.webkitMaskImage = "none";
        previewKamonFront.style.maskImage = "none";
      }

      // プレースホルダーの表示制御
      previewPlaceholderFront.style.display = hasContent ? "none" : "block";
    }

    // プレビューを更新（裏面）
    function updatePreviewBack() {
      const config = designConfigs[selectedOptionsBack.design];
      let hasContent = false;

      // === text1のリセットとコンテンツ設定 ===
      previewText1Back.classList.remove(
        "sashifuda-text",
        "senshafuda-double-border",
        "senshafuda-filled-double-border"
      );
      previewText1Back.style.border = "none";
      previewText1Back.style.backgroundColor = "transparent";
      previewText1Back.style.padding = "0";

      if (selectedOptionsBack.text && selectedOptionsBack.text.trim() !== "") {
        previewText1Back.textContent = selectedOptionsBack.text.replace(
          /[\r\n]+/g,
          ""
        );
        previewText1Back.style.color = selectedOptionsBack.color1;
        previewText1Back.style.display = "block";

        if (config && config.text1 && config.text1.applyStyles) {
          config.text1.applyStyles(
            previewText1Back,
            selectedOptionsBack,
            previewCanvasBack
          );
        }

        hasContent = true;
      } else {
        previewText1Back.style.display = "none";
      }

      // === text2のリセットとコンテンツ設定 ===
      if (config && config.textCount === 2) {
        previewText2Back.classList.remove("sashifuda-text");
        previewText2Back.style.border = "none";
        previewText2Back.style.backgroundColor = "transparent";
        previewText2Back.style.backgroundImage = "none";
        previewText2Back.style.padding = "0";

        if (
          selectedOptionsBack.text2 &&
          selectedOptionsBack.text2.trim() !== ""
        ) {
          previewText2Back.textContent = selectedOptionsBack.text2.replace(
            /[\r\n]+/g,
            ""
          );
          previewText2Back.style.color = selectedOptionsBack.color2;
          previewText2Back.style.display = "block";

          if (config.text2 && config.text2.applyStyles) {
            config.text2.applyStyles(
              previewText2Back,
              selectedOptionsBack,
              previewCanvasBack
            );
          }

          hasContent = true;
        } else {
          previewText2Back.style.display = "none";
        }
      } else {
        previewText2Back.style.display = "none";
      }

      // === 家紋画像の表示 ===
      if (
        selectedOptionsBack.kamonImage &&
        (selectedOptionsBack.design === "家紋" ||
          selectedOptionsBack.design === "梵字" ||
          selectedOptionsBack.design === "文字一行＋家紋" ||
          selectedOptionsBack.design === "文字一行＋家紋＋差札" ||
          selectedOptionsBack.design === "文字一行＋家紋＋色差札" ||
          selectedOptionsBack.design === "文字一行＋梵字" ||
          selectedOptionsBack.design === "文字一行＋梵字＋差札" ||
          selectedOptionsBack.design === "文字一行＋梵字＋色差札")
      ) {
        previewKamonBack.style.display = "block";

        if (
          selectedOptionsBack.kamonColor &&
          selectedOptionsBack.kamonColor !== "#000000"
        ) {
          previewKamonBack.style.backgroundColor =
            selectedOptionsBack.kamonColor;
          previewKamonBack.style.webkitMaskImage = `url('${selectedOptionsBack.kamonImage}')`;
          previewKamonBack.style.maskImage = `url('${selectedOptionsBack.kamonImage}')`;
          previewKamonBack.style.webkitMaskSize = "contain";
          previewKamonBack.style.maskSize = "contain";
          previewKamonBack.style.webkitMaskRepeat = "no-repeat";
          previewKamonBack.style.maskRepeat = "no-repeat";
          previewKamonBack.style.webkitMaskPosition = "center";
          previewKamonBack.style.maskPosition = "center";
          previewKamonBack.style.backgroundImage = "none";
        } else {
          previewKamonBack.style.backgroundImage = `url('${selectedOptionsBack.kamonImage}')`;
          previewKamonBack.style.backgroundColor = "transparent";
          previewKamonBack.style.webkitMaskImage = "none";
          previewKamonBack.style.maskImage = "none";
        }

        if (config && config.kamon) {
          previewKamonBack.style.top = config.kamon.position.top;
          previewKamonBack.style.left = config.kamon.position.left;
          previewKamonBack.style.width = config.kamon.size.width + "px";
          previewKamonBack.style.height = config.kamon.size.height + "px";
        } else {
          previewKamonBack.style.top = "50%";
          previewKamonBack.style.left = "50%";
          previewKamonBack.style.width = "45px";
          previewKamonBack.style.height = "45px";
        }

        hasContent = true;
      } else {
        previewKamonBack.style.display = "none";
        previewKamonBack.style.backgroundColor = "transparent";
        previewKamonBack.style.backgroundImage = "none";
        previewKamonBack.style.webkitMaskImage = "none";
        previewKamonBack.style.maskImage = "none";
      }

      // プレースホルダーの表示制御
      previewPlaceholderBack.style.display = hasContent ? "none" : "block";
    }

    // HEX色をCSSフィルターに変換する関数
    // 黒い画像(PNG/SVG)を指定された色に変換する
    function hexToFilter(hex) {
      // HEXをRGBに変換（0-255の範囲）
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);

      // 黒(#000000)の場合はフィルターなし
      if (r === 0 && g === 0 && b === 0) {
        return "none";
      }

      // RGB to HSL変換（0-1の範囲）
      const rNorm = r / 255;
      const gNorm = g / 255;
      const bNorm = b / 255;

      const max = Math.max(rNorm, gNorm, bNorm);
      const min = Math.min(rNorm, gNorm, bNorm);
      const l = (max + min) / 2;

      let h = 0;
      let s = 0;

      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case rNorm:
            h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
            break;
          case gNorm:
            h = ((bNorm - rNorm) / d + 2) / 6;
            break;
          case bNorm:
            h = ((rNorm - gNorm) / d + 4) / 6;
            break;
        }
      }

      // より正確な色変換のためのフィルター計算
      const hueRotate = Math.round(h * 360);
      const saturate = Math.round(s * 100);
      // 明度を調整
      const brightnessValue = Math.round(l * 2 * 100);

      // sepiaの量を調整（100だと黄色っぽくなる）
      let sepiaValue = 50;
      // 彩度が高い色の場合はsepiaを減らす
      if (s > 0.5) {
        sepiaValue = 30;
      }

      // 黒い画像を目的の色に変換するフィルター
      // sepia値を減らすことで、赤などの鮮やかな色をより正確に表現
      return `brightness(0) saturate(100%) invert(100%) sepia(${sepiaValue}%) saturate(${Math.max(
        saturate * 20,
        1000
      )}%) hue-rotate(${hueRotate}deg) brightness(${brightnessValue}%) contrast(100%)`;
    }

    // RGB色をHEX形式に変換する関数
    function rgbToHex(rgb) {
      // すでにHEX形式の場合はそのまま返す
      if (rgb.startsWith("#")) {
        return rgb;
      }

      // rgba(r, g, b, a) または rgb(r, g, b) 形式から数値を抽出
      const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!match) {
        return rgb; // 変換できない場合はそのまま返す
      }

      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);

      // HEX形式に変換
      const toHex = (n) => {
        const hex = n.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      };

      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    }

    // 画像スウォッチの監視（デザイン選択）
    function observeImageSwatches() {
      const imageSwatches = document.querySelectorAll(
        '[data-element-id*="image-swatches"] input'
      );

      imageSwatches.forEach((input) => {
        // 既にイベントリスナーが追加済みの場合はスキップ
        if (input.dataset.imageSwatchListenerAdded) return;
        input.dataset.imageSwatchListenerAdded = "true";

        input.addEventListener("change", function () {
          if (this.checked) {
            // フィールド名から裏面かどうかを判定
            const fieldName = this.dataset.fieldName || this.name || "";

            // 紐の種類・紐の色・カラースウォッチは除外（プレビューに影響しない）
            if (
              fieldName.includes("紐の種類") ||
              fieldName.includes("紐の色") ||
              this.closest('[data-element-id="image-swatches-9"]') ||
              this.closest('[data-element-id="image-swatches-10"]') ||
              this.closest('[data-element-id="image-swatches-11"]')
            ) {
              return;
            }
            const isBack = fieldName.includes("裏面");

            const label = this.nextElementSibling;
            const tooltip = label?.querySelector(".gpo-tooltip");
            let designName = "";

            if (tooltip) {
              // tooltipからデザイン名を取得（価格情報を除外）
              const fullText = tooltip.textContent?.trim() || "";

              // (+ ¥...) の部分を削除
              designName = fullText.replace(/\(\+\s*¥[0-9,]+\)/g, "").trim();
            } else if (label) {
              // ツールチップがない場合、labelから直接取得
              const breakWord = label.querySelector(".break-word");
              const addOn = label.querySelector(".addOn");

              if (breakWord) {
                designName = breakWord.textContent?.trim() || "";
              } else {
                designName = label.textContent?.trim() || "";
                // addOnテキストを除去
                if (addOn) {
                  designName = designName.replace(addOn.textContent, "").trim();
                }
              }

              // (+ ¥...) の部分を削除
              designName = designName.replace(/\(\+\s*¥[0-9,]+\)/g, "").trim();
            }

            if (designName) {
              // 「裏面のデザイン - 」のような接頭辞を削除
              designName = designName
                .replace(/^裏面のデザイン\s*-\s*/, "")
                .trim();
              // 「デザイン - 」のような接頭辞も削除
              designName = designName.replace(/^デザイン\s*-\s*/, "").trim();

              if (isBack) {
                // デザイン変更時に裏面の入力値をすべてクリア
                clearInputValues(true);
                selectedOptionsBack.design = designName;
                applyDesignConfigBack(designName);
                adjustTextScaleBack(1);
                adjustTextScaleBack(2);
                updatePreviewBack();
              } else {
                // デザイン変更時に表面の入力値をすべてクリア
                clearInputValues(false);
                selectedOptions.design = designName;
                applyDesignConfig(designName);
                adjustTextScale(1);
                adjustTextScale(2);
                updatePreview();
              }
            }
          }
        });

        // 初期選択の確認（ページ読み込み時のみ実行）
        if (input.checked && !input.dataset.initialChecked) {
          input.dataset.initialChecked = "true";
          input.dispatchEvent(new Event("change"));
        }
      });
    }

    // デザインに応じた設定を適用
    function applyDesignConfig(designName) {
      const config = designConfigs[designName];

      if (!config) {
        return;
      }

      // text1の設定を適用（すべてのデザインで統一）
      if (config.text1) {
        previewText1Front.style.top = config.text1.position.top;
        previewText1Front.style.left = config.text1.position.left;
        previewText1Front.style.writingMode = config.text1.writingMode;

        // 回転の適用
        selectedOptions.rotation = config.text1.rotation || 0;

        // スケール調整用のサイズを保存
        selectedOptions.maxWidth = config.text1.size.width;
        selectedOptions.maxHeight = config.text1.size.height;
        selectedOptions.writingMode = config.text1.writingMode;
      }

      if (config.textCount === 1) {
        // テキスト2は非表示
        previewText2Front.style.display = "none";
      } else if (config.textCount === 2) {
        // 2つのテキストの場合
        // テキスト1の設定（上で適用済み）
        // テキスト2の設定
        if (config.text2) {
          previewText2Front.style.top = config.text2.position.top;
          previewText2Front.style.left = config.text2.position.left;
          previewText2Front.style.writingMode = config.text2.writingMode;

          // スケール調整用のサイズを保存
          selectedOptions.text1Config = config.text1;
          selectedOptions.text2Config = config.text2;
        }
      }
    }

    // デザインに応じた設定を適用（裏面）
    function applyDesignConfigBack(designName) {
      const config = designConfigs[designName];

      if (!config) {
        return;
      }

      // text1の設定を適用（表面と同じ処理）
      if (config.text1) {
        previewText1Back.style.top = config.text1.position.top;
        previewText1Back.style.left = config.text1.position.left;
        previewText1Back.style.writingMode = config.text1.writingMode;

        // 回転の適用
        selectedOptionsBack.rotation = config.text1.rotation || 0;

        // スケール調整用のサイズを保存
        selectedOptionsBack.maxWidth = config.text1.size.width;
        selectedOptionsBack.maxHeight = config.text1.size.height;
        selectedOptionsBack.writingMode = config.text1.writingMode;
      }

      if (config.textCount === 1) {
        // テキスト2は非表示
        previewText2Back.style.display = "none";
      } else if (config.textCount === 2) {
        // 2つのテキストの場合
        // テキスト2の設定
        if (config.text2) {
          previewText2Back.style.top = config.text2.position.top;
          previewText2Back.style.left = config.text2.position.left;
          previewText2Back.style.writingMode = config.text2.writingMode;

          // スケール調整用のサイズを保存
          selectedOptionsBack.text1Config = config.text1;
          selectedOptionsBack.text2Config = config.text2;
        }
      }
    }

    // カラースウォッチの監視
    function observeColorSwatches() {
      const colorSwatches = document.querySelectorAll(".color-swatches input");

      colorSwatches.forEach((input) => {
        // 既にイベントリスナーが追加済みの場合はスキップ
        if (input.dataset.colorSwatchListenerAdded) return;
        input.dataset.colorSwatchListenerAdded = "true";

        input.addEventListener("change", function () {
          if (this.checked) {
            const label = this.nextElementSibling;
            const colorDiv = label?.querySelector(".gpo-swatch__inner");

            if (colorDiv) {
              // 背景色を取得（複数の方法を試す）
              let backgroundColor =
                window.getComputedStyle(colorDiv).backgroundColor;

              // 透明な場合は、親要素や data 属性から取得を試みる
              if (
                backgroundColor === "rgba(0, 0, 0, 0)" ||
                backgroundColor === "transparent"
              ) {
                // data-color 属性を確認
                backgroundColor =
                  colorDiv.dataset.color ||
                  label.dataset.color ||
                  input.dataset.color;

                // それでもない場合は、style属性を確認
                if (
                  !backgroundColor ||
                  backgroundColor === "rgba(0, 0, 0, 0)"
                ) {
                  backgroundColor = colorDiv.style.backgroundColor;
                }

                // まだ透明な場合は、tooltipから色名を取得して色を推定
                if (
                  !backgroundColor ||
                  backgroundColor === "rgba(0, 0, 0, 0)" ||
                  backgroundColor === "transparent"
                ) {
                  const tooltip = label.querySelector(".gpo-tooltip");
                  if (tooltip) {
                    // ツールチップから色名のみを抽出（価格などを除外）
                    let colorName = tooltip.textContent?.trim() || "";

                    // 括弧や価格情報を除去
                    colorName = colorName.split(/[(\uff08]/)[0].trim();
                    colorName = colorName.split(/[+＋]/)[0].trim();
                    colorName = colorName.split(/¥/)[0].trim();

                    // 色名から色コードをマッピング（より多くの色に対応）
                    const colorMap = {
                      黒: "#000000",
                      白: "#FFFFFF",
                      赤: "#E60012",
                      桃: "#F19EC2",
                      緑: "#064738",
                      黄緑: "#00A040",
                      黄: "#F6AB00",
                      藍: "#0E3962",
                      金: "#CCA11F",
                      銀: "#C9CACA",
                    };
                    backgroundColor = colorMap[colorName] || "#000000";
                  }
                }
              }

              // RGB形式の場合はHEX形式に変換
              if (backgroundColor && !backgroundColor.startsWith("#")) {
                backgroundColor = rgbToHex(backgroundColor);
              }

              // フィールド名に応じて適切な色に割り当て
              const fieldName = input.dataset.fieldName || input.name || "";
              const isBack = fieldName.includes("裏面");
              const options = isBack ? selectedOptionsBack : selectedOptions;

              if (fieldName.includes("横書追加")) {
                options.color2 = backgroundColor || "#000000";
              } else if (
                fieldName.includes("縦一行追加") ||
                fieldName.includes("一行追加")
              ) {
                options.color2 = backgroundColor || "#000000";
              } else if (fieldName.includes("縦一行追加")) {
                options.color2 = backgroundColor || "#000000";
              } else if (
                fieldName.includes("差札") &&
                !fieldName.includes("色差札") &&
                (fieldName.includes("枠") ||
                  fieldName.includes("線") ||
                  fieldName.includes("2"))
              ) {
                options.borderColor = backgroundColor || "#000000";
              } else if (
                fieldName.includes("差札") &&
                !fieldName.includes("色差札") &&
                !fieldName.includes("枠") &&
                !fieldName.includes("線")
              ) {
                if (
                  options.design === "文字一行＋差札" ||
                  options.design === "文字一行＋家紋＋差札" ||
                  options.design === "文字一行＋梵字＋差札"
                ) {
                  options.color2 = backgroundColor || "#000000";
                } else {
                  options.color1 = backgroundColor || "#000000";
                }
              } else if (
                fieldName.includes("色差札") &&
                (fieldName.includes("背景") || fieldName.includes("2"))
              ) {
                options.backgroundColor = backgroundColor || "#E60012";
              } else if (
                fieldName.includes("色差札") &&
                !fieldName.includes("背景") &&
                !fieldName.includes("枠") &&
                !fieldName.includes("線")
              ) {
                if (
                  options.design === "文字一行＋色差札" ||
                  options.design === "文字一行＋家紋＋色差札" ||
                  options.design === "文字一行＋梵字＋色差札"
                ) {
                  options.color2 = backgroundColor || "#000000";
                } else {
                  options.color1 = backgroundColor || "#000000";
                }
              } else if (
                fieldName.includes("色千社札") &&
                (fieldName.includes("背景") || fieldName.includes("2"))
              ) {
                options.backgroundColor = backgroundColor || "#E60012";
              } else if (
                fieldName.includes("色千社札") &&
                (fieldName.includes("枠") ||
                  fieldName.includes("線") ||
                  fieldName.includes("3"))
              ) {
                options.borderColor = backgroundColor || "#000000";
              } else if (
                fieldName.includes("千社札") &&
                !fieldName.includes("色千社札") &&
                (fieldName.includes("枠") ||
                  fieldName.includes("線") ||
                  fieldName.includes("2"))
              ) {
                options.borderColor = backgroundColor || "#000000";
              } else if (
                (fieldName.includes("家紋") || fieldName.includes("梵字")) &&
                fieldName.includes("色")
              ) {
                options.kamonColor = backgroundColor || "#000000";
              } else if (
                fieldName.includes("文字一行") ||
                fieldName.includes("差札") ||
                fieldName.includes("色差札") ||
                fieldName.includes("千社札") ||
                fieldName.includes("色千社札")
              ) {
                options.color1 = backgroundColor || "#000000";
              }

              // 表面・裏面それぞれのプレビューを更新
              if (isBack) {
                updatePreviewBack();
              } else {
                updatePreview();
              }
            }
          }
        });

        // 初期選択の確認
        if (input.checked) {
          input.dispatchEvent(new Event("change"));
        }
      });
    }

    // ドロップダウンの監視（家紋など）
    function observeDropdowns() {
      const dropdowns = document.querySelectorAll(".gpo-dropdown");

      dropdowns.forEach((dropdown, index) => {
        // このドロップダウンが既に処理済みかチェック
        if (dropdown.dataset.dropdownObserved === "true") {
          return;
        }

        const items = dropdown.querySelectorAll(
          'input[type="radio"], input[type="checkbox"]'
        );

        if (items.length === 0) return;

        // 最初のinputから裏面フィールドかどうかを判定
        const firstInput = items[0];
        const firstFieldName =
          firstInput.dataset.fieldName || firstInput.name || "";
        const isBackField = firstFieldName.includes("裏面");

        let isKamonDropdown = false;
        let isBonjiDropdown = false;

        // このドロップダウンが家紋用または梵字用かどうか判定（最初の数項目をチェック）
        for (let i = 0; i < Math.min(items.length, 5); i++) {
          const label = items[i].nextElementSibling;
          if (label) {
            const rawText = label.textContent.trim();
            // 全角・半角の括弧で分割してテキストを取得
            const splitText = rawText.split(/[(\uff08]/)[0].trim();

            if (typeof kamonMap !== "undefined" && kamonMap[splitText]) {
              isKamonDropdown = true;
              break;
            }
            if (typeof bonjiMap !== "undefined") {
              if (bonjiMap[rawText] || bonjiMap[splitText]) {
                isBonjiDropdown = true;
                break;
              }
            }
          }
        }

        if (isKamonDropdown || isBonjiDropdown) {
          // このドロップダウンを処理済みとしてマーク
          dropdown.dataset.dropdownObserved = "true";

          const options = isBackField ? selectedOptionsBack : selectedOptions;
          const updateFunc = isBackField ? updatePreviewBack : updatePreview;

          items.forEach((input) => {
            // 重複登録防止
            if (input.dataset.hasKamonListener) return;
            input.dataset.hasKamonListener = "true";

            input.addEventListener("change", function () {
              if (this.checked) {
                const label = this.nextElementSibling;
                const rawText = label ? label.textContent.trim() : "";
                // 全角・半角の括弧で分割してテキストを取得
                const splitText = rawText.split(/[(\uff08]/)[0].trim();

                if (typeof kamonMap !== "undefined" && kamonMap[splitText]) {
                  options.kamonImage = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${kamonMap[splitText]}`;
                } else if (typeof bonjiMap !== "undefined") {
                  // 梵字はフルテキストまたは分割テキストの両方を試す
                  if (bonjiMap[rawText]) {
                    options.kamonImage = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${bonjiMap[rawText]}`;
                  } else if (bonjiMap[splitText]) {
                    options.kamonImage = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${bonjiMap[splitText]}`;
                  } else {
                    options.kamonImage = "";
                  }
                } else {
                  // 家紋以外（「なし」など）が選択された場合
                  options.kamonImage = "";
                }

                updateFunc();
              }
            });

            // 初期選択の確認（初回のみ）
            if (input.checked && !input.dataset.initialCheckDone) {
              input.dataset.initialCheckDone = "true";
              const label = input.nextElementSibling;
              const text = label
                ? label.textContent.trim().split("(")[0].trim()
                : "";

              if (typeof kamonMap !== "undefined" && kamonMap[text]) {
                options.kamonImage = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${kamonMap[text]}`;
              } else if (typeof bonjiMap !== "undefined" && bonjiMap[text]) {
                options.kamonImage = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${bonjiMap[text]}`;
              }
            }
          });
        }
      });
    }

    // テキスト入力の監視
    function observeTextInputs() {
      // Globo Product Optionsのテキスト入力フィールドを監視
      const textInputs = document.querySelectorAll(
        'input[type="text"].gpo-input, textarea.gpo-input'
      );

      // 各テキスト入力を監視
      textInputs.forEach((input, index) => {
        const fieldName = input.dataset.fieldName || input.name || "";

        // 既にイベントリスナーが追加済みの場合はスキップ
        if (input.dataset.textInputListenerAdded) return;
        input.dataset.textInputListenerAdded = "true";

        input.addEventListener("input", function () {
          const value = this.value;

          const isBack = fieldName.includes("裏面");
          const options = isBack ? selectedOptionsBack : selectedOptions;
          const updateFunc = isBack ? updatePreviewBack : updatePreview;
          const adjustFunc = isBack ? adjustTextScaleBack : adjustTextScale;

          // 家紋名のテキストボックスはプレビューに反映しない
          if (fieldName.includes("家紋")) {
            return;
          }

          // フィールド名に応じて適切なテキストに割り当て
          let isText2 = false;

          if (
            fieldName.includes("縦一行追加") ||
            fieldName.includes("一行追加") ||
            fieldName.includes("横書追加")
          ) {
            isText2 = true;
          } else if (fieldName.includes("差札")) {
            // 差札の場合、デザインが2テキスト持ちならtext2として扱う
            const config = designConfigs[options.design];
            if (config && config.textCount === 2) {
              isText2 = true;
            }
          }

          if (isText2) {
            options.text2 = value;
            adjustFunc(2);
          } else {
            options.text = value;
            adjustFunc(1);
          }
          updateFunc();
        });

        // 初期値の確認
        if (input.value) {
          const isBack = fieldName.includes("裏面");
          const options = isBack ? selectedOptionsBack : selectedOptions;

          // フィールド名に応じて適切なテキストに割り当て
          let isText2 = false;

          if (
            fieldName.includes("縦一行追加") ||
            fieldName.includes("一行追加") ||
            fieldName.includes("横書追加")
          ) {
            isText2 = true;
          } else if (fieldName.includes("差札")) {
            const config = designConfigs[options.design];
            if (config && config.textCount === 2) {
              isText2 = true;
            }
          }

          if (isText2) {
            options.text2 = input.value;
          } else {
            options.text = input.value;
          }
        }
      });
    }

    // 文字数に応じてテキストの縦横比を自動調整
    // 文字数に応じてテキストの縦横比を自動調整
    function adjustTextScale(textNumber = 1) {
      const config = designConfigs[selectedOptions.design];
      if (!config) return;

      // text1またはtext2の設定を取得（すべてtext1/text2構造に統一）
      const itemConfig = textNumber === 1 ? config.text1 : config.text2;
      if (!itemConfig) return;

      const text =
        textNumber === 1 ? selectedOptions.text : selectedOptions.text2;
      const textElement =
        textNumber === 1 ? previewText1Front : previewText2Front;

      const maxWidth = itemConfig.size.width;
      const maxHeight = itemConfig.size.height;
      const writingMode = itemConfig.writingMode;

      // 改行を削除してから文字数をカウント
      const textWithoutBreaks = text ? text.replace(/[\r\n]+/g, "") : "";
      const textLength = textWithoutBreaks.length;

      // 基準フォントサイズ
      const baseFontSize = 40;

      let scaleX = 1;
      let scaleY = 1;

      if (writingMode === "vertical-rl") {
        // 縦書きの場合（高さ方向に文字が並ぶ）
        if (textLength === 0) {
          scaleX = 1;
          scaleY = 1;
        } else if (textLength === 1) {
          scaleY = maxHeight / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 2) {
          scaleY = maxHeight / 2 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 3) {
          scaleY = maxHeight / 3 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 4) {
          scaleY = maxHeight / 4 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 5) {
          scaleY = maxHeight / 5 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength <= 7) {
          scaleY = maxHeight / textLength / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else {
          scaleY = maxHeight / textLength / baseFontSize;
          scaleX = (maxWidth / baseFontSize) * 0.9;
        }
      } else {
        // 横書きの場合（幅方向に文字が並ぶ）
        if (textLength === 0) {
          scaleX = 1;
          scaleY = 1;
        } else {
          scaleX = maxWidth / textLength / baseFontSize;
          scaleY = maxHeight / baseFontSize;

          // 最小スケールを設定
          scaleX = Math.max(scaleX, 0.3);
          scaleY = Math.max(scaleY, 0.3);
        }
      }

      // 回転の取得
      const rotation = itemConfig.rotation || 0;

      // テキスト要素にスタイルを適用（回転 → スケールの順）
      // 回転を先に適用することで、枠線が歪まない ようにする
      textElement.style.fontSize = baseFontSize + "px";
      textElement.style.transform = `translate(-50%, -50%) rotate(${rotation}deg) scale(${scaleX}, ${scaleY})`;

      // スケール値をCSS変数として設定（枠線の太さ補正用）
      textElement.style.setProperty("--scale-x", scaleX);
      textElement.style.setProperty("--scale-y", scaleY);

      // box-sizingを設定して枠線が内側に含まれるようにする
      textElement.style.boxSizing = "border-box";
    }

    // 文字数に応じてテキストの縦横比を自動調整（裏面）
    function adjustTextScaleBack(textNumber = 1) {
      const config = designConfigs[selectedOptionsBack.design];
      if (!config) return;

      const itemConfig = textNumber === 1 ? config.text1 : config.text2;
      if (!itemConfig) return;

      const text =
        textNumber === 1 ? selectedOptionsBack.text : selectedOptionsBack.text2;
      const textElement =
        textNumber === 1 ? previewText1Back : previewText2Back;

      const maxWidth = itemConfig.size.width;
      const maxHeight = itemConfig.size.height;
      const writingMode = itemConfig.writingMode;

      const textWithoutBreaks = text ? text.replace(/[\r\n]+/g, "") : "";
      const textLength = textWithoutBreaks.length;

      const baseFontSize = 40;

      let scaleX = 1;
      let scaleY = 1;

      if (writingMode === "vertical-rl") {
        if (textLength === 0) {
          scaleX = 1;
          scaleY = 1;
        } else if (textLength === 1) {
          scaleY = maxHeight / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 2) {
          scaleY = maxHeight / 2 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 3) {
          scaleY = maxHeight / 3 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 4) {
          scaleY = maxHeight / 4 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength === 5) {
          scaleY = maxHeight / 5 / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else if (textLength <= 7) {
          scaleY = maxHeight / textLength / baseFontSize;
          scaleX = maxWidth / baseFontSize;
        } else {
          scaleY = maxHeight / textLength / baseFontSize;
          scaleX = (maxWidth / baseFontSize) * 0.9;
        }
      } else {
        if (textLength === 0) {
          scaleX = 1;
          scaleY = 1;
        } else {
          scaleX = maxWidth / textLength / baseFontSize;
          scaleY = maxHeight / baseFontSize;

          scaleX = Math.max(scaleX, 0.3);
          scaleY = Math.max(scaleY, 0.3);
        }
      }

      const rotation = itemConfig.rotation || 0;

      textElement.style.fontSize = baseFontSize + "px";
      textElement.style.transform = `translate(-50%, -50%) rotate(${rotation}deg) scale(${scaleX}, ${scaleY})`;

      textElement.style.setProperty("--scale-x", scaleX);
      textElement.style.setProperty("--scale-y", scaleY);

      textElement.style.boxSizing = "border-box";
    }

    // すべてのオプションを監視
    function initializeAllObservers() {
      // 少し遅延して実行（Globo Product Optionsの要素が完全に読み込まれるまで待つ）
      setTimeout(() => {
        observeImageSwatches();
        observeColorSwatches();
        observeTextInputs();
        observeDropdowns();
        updatePreview();
        updatePreviewBack();
      }, 500);

      // さらに遅延して再実行（動的に追加される要素に対応）
      setTimeout(() => {
        observeImageSwatches();
        observeColorSwatches();
        observeTextInputs();
        observeDropdowns();
      }, 2000);
    }

    // デバウンスタイマー
    let observerDebounceTimer = null;

    // MutationObserverでGlobo Product Optionsの要素追加を監視
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // プレビューコンテナの追加はスキップ（無限ループ防止）
              if (
                node.classList &&
                node.classList.contains("kifuda-preview-container")
              ) {
                return;
              }

              // 新しいオプション要素が追加された場合
              if (
                node.classList?.contains("gpo-swatches") ||
                node.classList?.contains("color-swatches") ||
                node.querySelector?.(
                  ".gpo-swatches, .color-swatches, .gpo-input"
                )
              ) {
                shouldUpdate = true;
              }
            }
          });
        }
      });

      // デバウンス処理：短時間に複数回呼ばれても1回だけ実行
      if (shouldUpdate) {
        if (observerDebounceTimer) {
          clearTimeout(observerDebounceTimer);
        }
        observerDebounceTimer = setTimeout(() => {
          observeImageSwatches();
          observeColorSwatches();
          observeTextInputs();
          observeDropdowns();
          observerDebounceTimer = null;
        }, 300);
      }
    }); // body全体を監視
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // 初期化
    initializeAllObservers();
  } catch (error) {
    console.error("木札ライブプレビュー機能のエラー:", error);
  }
}

/**
 * ドロップダウン検索機能
 */
function initDropdownSearch() {
  try {
    // 処理中フラグ（重複実行防止）
    let isProcessing = false;

    // 画像プリロード管理（同じURLは1回だけ読み込む）
    const imagePreloadMap = new Map();

    // 画像をプリロードする関数
    function preloadImage(url) {
      if (!imagePreloadMap.has(url)) {
        const img = new Image();
        img.src = url;
        imagePreloadMap.set(url, img);
      }
      return imagePreloadMap.get(url);
    }

    // すべてのドロップダウン要素を取得
    function addSearchToDropdowns() {
      // 既に処理中の場合はスキップ
      if (isProcessing) {
        return;
      }
      isProcessing = true;

      const dropdowns = document.querySelectorAll(".gpo-dropdown");
      dropdowns.forEach((dropdown, index) => {
        const dropdownList = dropdown.querySelector(".dropdown-list");
        if (!dropdownList) {
          return;
        }

        // 既に処理済みの場合はスキップ（重複防止）
        if (dropdown.dataset.searchAdded === "true") {
          return;
        }

        dropdown.dataset.searchAdded = "true";

        // 既存の検索ボックスを削除（再作成のため）
        const existingSearch = dropdown.querySelector(
          ".gpo-dropdown-search-wrapper"
        );
        const existingNoResults = dropdown.querySelector(
          ".gpo-dropdown-no-results"
        );
        if (existingSearch) {
          existingSearch.remove();
        }
        if (existingNoResults) {
          existingNoResults.remove();
        }

        // 検索ボックスのラッパーを作成
        const searchWrapper = document.createElement("div");
        searchWrapper.className = "gpo-dropdown-search-wrapper";
        searchWrapper.style.cssText =
          "padding: 5px; border-bottom: 1px solid #ddd; background: #f9f9f9;";

        // 検索ボックスエリアのクリックでドロップダウンが閉じないようにする
        searchWrapper.addEventListener("click", function (e) {
          e.stopPropagation();
        });

        searchWrapper.addEventListener("mousedown", function (e) {
          e.stopPropagation();
        });

        // 検索入力フィールドを作成
        const searchInput = document.createElement("input");
        searchInput.type = "text";
        searchInput.className = "gpo-dropdown-search";
        searchInput.placeholder = "検索...";
        searchInput.style.cssText =
          "width: 100%; padding: 5px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; box-sizing: border-box;";

        // 検索ボックスのクリックでドロップダウンが閉じないようにする
        searchInput.addEventListener("click", function (e) {
          e.stopPropagation();
        });

        searchInput.addEventListener("mousedown", function (e) {
          e.stopPropagation();
        });

        searchInput.addEventListener("focus", function (e) {
          e.stopPropagation();
        });

        // 検索結果なしメッセージを作成
        const noResultsMessage = document.createElement("div");
        noResultsMessage.className = "gpo-dropdown-no-results";
        noResultsMessage.textContent = "該当する項目が見つかりません";
        noResultsMessage.style.cssText =
          "padding: 12px; text-align: center; color: #999; font-size: 14px; display: none;";

        searchWrapper.appendChild(searchInput);

        // ドロップダウンリストの最初に検索ボックスを挿入
        dropdownList.insertBefore(searchWrapper, dropdownList.firstChild);
        dropdownList.appendChild(noResultsMessage);

        // すべてのドロップダウン項目を取得
        const items = dropdown.querySelectorAll(
          'input[type="radio"], input[type="checkbox"]'
        );

        // 画像をバッチ処理で追加（メモリ節約）
        const BATCH_SIZE = 50;
        const addImagesInBatches = (startIndex) => {
          const endIndex = Math.min(startIndex + BATCH_SIZE, items.length);

          for (let i = startIndex; i < endIndex; i++) {
            const item = items[i];
            const label = item.nextElementSibling;
            if (!label) continue;

            // 既存の画像要素をチェック
            if (label.querySelector(".dropdown-item-image")) continue;

            // ラベルのテキストからファイル名を生成
            const labelText = label.textContent.trim();
            // テキストから価格などを除去してファイル名を取得
            // 全角・半角の括弧で分割
            const cleanText = labelText.split(/[(\uff08]/)[0].trim(); // 例: "青山銭 (+ ¥1,000)" → "青山銭"

            // assetsフォルダから画像を読み込む
            let imageUrl = "";

            // kamon-map.js の kamonMap を使用
            if (typeof kamonMap !== "undefined" && kamonMap[cleanText]) {
              imageUrl = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${kamonMap[cleanText]}`;
            } else if (typeof bonjiMap !== "undefined") {
              if (bonjiMap[cleanText]) {
                // bonji-map.js の bonjiMap を使用
                imageUrl = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${bonjiMap[cleanText]}`;
              } else {
              }
            } else {
              // マッピングがない場合は元のテキストを使用
              const encodedFileName = encodeURIComponent(cleanText);
              imageUrl = `https://edomojiyaouan.com/cdn/shop/t/9/assets/${encodedFileName}.png`;
            }

            // 画像をプリロード（同じURLは1回だけ読み込まれる）
            preloadImage(imageUrl);

            // 画像要素を作成（プリロード済みなのでキャッシュから即座に表示）
            const imgElement = document.createElement("img");
            imgElement.className = "dropdown-item-image";
            imgElement.alt = cleanText;
            imgElement.loading = "lazy";
            imgElement.src = imageUrl;

            // エラー時の処理
            imgElement.onerror = function () {
              this.style.display = "none";
            };

            // ラベルの最初に画像を挿入
            label.insertBefore(imgElement, label.firstChild);
          }

          // 次のバッチを処理
          if (endIndex < items.length) {
            setTimeout(() => addImagesInBatches(endIndex), 100);
          }
        };

        // 画像追加を開始
        if (items.length > 0) {
          addImagesInBatches(0);
        }

        // 検索機能の実装
        searchInput.addEventListener("input", function () {
          const searchTerm = this.value.toLowerCase().trim();
          let visibleCount = 0;

          items.forEach((item) => {
            const label = item.nextElementSibling;
            if (!label) return;

            const text = label.textContent.toLowerCase();
            const isMatch = text.includes(searchTerm);

            // 親要素の .dropdown-item を取得して非表示にする
            const dropdownItem = item.closest(".dropdown-item");

            if (isMatch) {
              item.setAttribute("data-hidden", "false");
              if (dropdownItem) {
                dropdownItem.style.display = "";
              } else {
                label.style.display = "";
              }
              visibleCount++;
            } else {
              item.setAttribute("data-hidden", "true");
              if (dropdownItem) {
                dropdownItem.style.display = "none";
              } else {
                label.style.display = "none";
              }
            }
          });

          // 検索結果がない場合のメッセージ表示
          if (visibleCount === 0) {
            noResultsMessage.style.display = "block";
          } else {
            noResultsMessage.style.display = "none";
          }
        });

        // ドロップダウンが開かれた時に検索ボックスにフォーカス
        const dropdownButton = dropdown.querySelector(".dropdown-button");
        if (dropdownButton) {
          dropdownButton.addEventListener("click", function () {
            setTimeout(() => {
              const isOpen =
                dropdown.classList.contains("active") ||
                dropdownList.style.display !== "none";
              if (isOpen) {
                // 検索をリセット
                searchInput.value = "";
                items.forEach((item) => {
                  item.setAttribute("data-hidden", "false");
                  const dropdownItem = item.closest(".dropdown-item");
                  if (dropdownItem) {
                    dropdownItem.style.display = "";
                  } else {
                    const label = item.nextElementSibling;
                    if (label) label.style.display = "";
                  }
                });
                noResultsMessage.style.display = "none";
                // 検索ボックスにフォーカス
                searchInput.focus();
              }
            }, 150);
          });
        }
      });

      // 処理完了
      isProcessing = false;
    }

    // 初回実行（1回だけ実行し、後はMutationObserverに任せる）
    setTimeout(() => {
      addSearchToDropdowns();
    }, 1000);

    // デバウンスタイマー
    let dropdownObserverDebounceTimer = null;

    // MutationObserverで動的に追加されるドロップダウンを監視
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // ドロップダウンの画像や検索ボックスの追加はスキップ（無限ループ防止）
              if (
                node.classList &&
                (node.classList.contains("dropdown-item-image") ||
                  node.classList.contains("gpo-dropdown-search-wrapper") ||
                  node.classList.contains("gpo-dropdown-no-results"))
              ) {
                return;
              }

              if (
                node.classList?.contains("gpo-dropdown") ||
                node.querySelector?.(".gpo-dropdown") ||
                node.classList?.contains("dropdown-list")
              ) {
                shouldUpdate = true;
              }
            }
          });
        }
      });

      // デバウンス処理：短時間に複数回呼ばれても1回だけ実行
      if (shouldUpdate) {
        if (dropdownObserverDebounceTimer) {
          clearTimeout(dropdownObserverDebounceTimer);
        }
        dropdownObserverDebounceTimer = setTimeout(() => {
          addSearchToDropdowns();
          dropdownObserverDebounceTimer = null;
        }, 400);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  } catch (error) {
    console.error("ドロップダウン検索機能のエラー:", error);
  }
}
